export interface TreasuryAccount {
  id?: number;
  companyId?: number;
  name?: string;
  description?: string;
  initialBalance?: number;
  balance?: number;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const newTreasuryAccount = (): TreasuryAccount => {
  return {};
};

export interface TreasuryAccountEntry {
  id: number;
  accountId: number;
  movementId?: number;
  action: string;
  amount: number;
  balanceAfter: number;
  userName?: string;
  obs?: string;
  createdAt: string;
  movementType?: string;
  movementDescription?: string;
}

export interface TreasuryMovement {
  id: number;
  companyId?: number;
  movementDate: string;
  movementType: string;
  sourceType: string;
  sourceId?: number;
  paymentMethod?: string;
  amount: number;
  description?: string;
  status: string;
  accountId?: number;
  accountName?: string;
  suggestedAccountId?: number;
  suggestedAccountName?: string;
  reversesMovementId?: number;
  // Reversion generada porque el origen de este movimiento se anulo.
  reversedByMovementId?: number;
  // En una reversion: estado del movimiento original que revierte.
  reversedMovementStatus?: string;
  reason?: string;
  createdByUserName?: string;
  resolvedByUserName?: string;
  resolvedAt?: string;
}

export interface TreasuryAccountOption {
  id: number;
  name: string;
}

export interface TreasuryMovementLog {
  id: number;
  movementId: number;
  action: string;
  userName?: string;
  changes?: string;
  createdAt: string;
}

export const MOVEMENT_SOURCE_MANUAL = "manual";

export interface TreasuryMovementsTotals {
  count: number;
  totalIn: number;
  totalOut: number;
  net: number;
}

export const MOVEMENT_STATUS_PENDING = "pending";
export const MOVEMENT_STATUS_ASSIGNED = "assigned";
export const MOVEMENT_STATUS_DISCARDED = "discarded";
export const MOVEMENT_STATUS_ANNULLED = "annulled";

export const MOVEMENT_STATUSES: Map<string, { label: string; color: string }> =
  new Map([
    [MOVEMENT_STATUS_PENDING, { label: "Pendiente", color: "warning" }],
    [MOVEMENT_STATUS_ASSIGNED, { label: "Aceptado", color: "success" }],
    [MOVEMENT_STATUS_DISCARDED, { label: "Descartado", color: "secondary" }],
    [MOVEMENT_STATUS_ANNULLED, { label: "Anulado", color: "dark" }],
  ]);

// Los automaticos (venta, cobro, gasto...) ya no se generan; quedan para los
// movimientos viejos.
export const MOVEMENT_TYPES: Map<string, string> = new Map([
  ["manual", "Movimiento manual"],
  ["sale", "Venta"],
  ["sale_return", "Devolución"],
  ["client_payment", "Cobro a cliente"],
  ["expense", "Gasto"],
  ["reversal", "Reversión"],
  ["manual_adjustment", "Ajuste manual"],
]);

// Los medios de pago llegan con los valores de cada origen: el POS usa
// "transfer" y "qr_payment", los cobros a clientes "bank_transfer" y
// "qr_code". El filtro agrupa los equivalentes.
export const PAYMENT_METHOD_FILTERS: Map<string, { label: string; values: string[] }> =
  new Map([
    ["cash", { label: "Efectivo", values: ["cash"] }],
    ["credit_card", { label: "Tarjeta de crédito", values: ["credit_card"] }],
    ["debit_card", { label: "Tarjeta de débito", values: ["debit_card"] }],
    [
      "transfer",
      { label: "Transferencia", values: ["transfer", "bank_transfer"] },
    ],
    ["qr", { label: "Pago QR", values: ["qr_payment", "qr_code"] }],
    ["check", { label: "Cheque", values: ["check"] }],
  ]);

export const getPaymentMethodLabel = (value?: string): string => {
  if (!value) {
    return "-";
  }
  for (const filter of Array.from(PAYMENT_METHOD_FILTERS.values())) {
    if (filter.values.indexOf(value) >= 0) {
      return filter.label;
    }
  }
  return value;
};

export const ENTRY_ACTIONS: Map<string, string> = new Map([
  ["initial", "Creación / saldo inicial"],
  ["assign", "Aceptación"],
  ["unassign", "Aprobación revertida"],
  ["reassign_out", "Reasignación (salida)"],
  ["reassign_in", "Reasignación (entrada)"],
  ["discard_after_assign", "Descarte luego de aceptado"],
  ["adjustment", "Ajuste manual"],
  ["update", "Modificación de la cuenta"],
  ["deactivate", "Baja de la cuenta"],
  ["activate", "Reactivación de la cuenta"],
]);

export const MOVEMENT_LOG_ACTIONS: Map<string, string> = new Map([
  ["created", "Carga"],
  ["updated", "Edición"],
  ["accepted", "Aceptación"],
  ["reassigned", "Reasignación"],
  ["discarded", "Descarte"],
  ["reopened", "Vuelta a pendiente"],
  ["adjustment", "Ajuste manual"],
]);

export default TreasuryAccount;
